import {$t} from "@/i18n";
import {
    formatDate,
    formatSysDate,
    formatTime,
    formatTimeDuration,
    parseSysDate,
    parseSysTime,
    formatSysTime,
    formatSysDateTime,
    parseSysDateTime,
    parseTime, parseTimeDuration
} from "@/plugins/formatting";
import date from "@/components/RichDataTable/components/Date";

export default class Model {
    constructor(isMobile, adminView, editTaskId, runningTaskId, currentPersonId, isReadOnly, editTaskVirtualId) {
        this.timeEntries = []
        this.isMobile = isMobile
        this.adminView = adminView
        this.editTaskId = editTaskId
        this.runningTaskId = runningTaskId
        this.currentPersonId = currentPersonId
        this.lastTimeEntry = null
        this.isReadOnly = isReadOnly
        this.editTimeEntryIndex = null
        this.timeEntryErrors = []
        this.editTaskVirtualId = editTaskVirtualId
    }

    getHeaders () {
        let ret = [
            {
                value: 'billable',
                title: '',
                width: this.isMobile ? 22 : 50
            },
        ]

        if (!this.isMobile) {
            ret.push({
                value: 'person_or_new_text',
                title: this.adminView ? $t('Person') : '',
                width: 142
            })
        }

        ret = ret.concat([
            {
                value: 'date',
                title: $t('Date'),
                width: this.isMobile ? 88 : 106
            },
            {
                value: 'start',
                title: $t('Start'),
                width: this.isMobile ? 50 : 92
            },
            {
                value: 'end',
                title: $t('End'),
                width: this.isMobile ? 50 : 92
            },
            {
                value: 'duration',
                title: $t('Duration'),
                width: this.isMobile ? 64 : 102
            },
            {
                value: 'button',
                title: '',
                width: 0
            }
        ])

        return ret
    }

    getBillableTimeWithRunningTimeEntryBillableTime() {
        let total = 0

        for (let timeEntry of this.timeEntries) {
            if (timeEntry.running) continue;
            if (timeEntry.billable) total += timeEntry.duration;
        }

        if (this.isEditTaskRunning() && this.lastTimeEntry && this.lastTimeEntry.billable) {
            total += this.lastTimeEntry.duration
        }

        return total
    }

    getNonBillableTimeWithRunningTimeEntryNonBillableTime() {
        let total = 0

        for (let timeEntry of this.timeEntries) {
            if (timeEntry.running) continue;
            if (!timeEntry.billable) total += timeEntry.duration;
        }

        if (this.isEditTaskRunning() && this.lastTimeEntry && !this.lastTimeEntry.billable) {
            total += this.lastTimeEntry.duration
        }

        return total
    }

    isEditTaskRunning() {
        if (this.timeEntries.length === 0) return false;
        if (this.editTaskId !== this.runningTaskId) return false;
        return true
    }

    addEmptyRow(company) {
        const billable = company && company.billing_settings ? company.billing_settings.time_billable : 1

        this.timeEntries.unshift({
            id: 0,
            person_id: this.currentPersonId,
            end: formatSysDate(new Date()),
            duration: null,
            billable: billable,
            start: formatSysDate(new Date()),
            dirty: false,
            running: false,
            task_id: this.editTaskId
        });
    }

    toggleBillable (timeEntry) {
        timeEntry.billable = !timeEntry.billable;
        timeEntry.dirty = true;
    };

    datePicked (timeEntry, date) {
        timeEntry.dirty = true;
        timeEntry.start = this.overrideDate(timeEntry.start, date);
        timeEntry.end = this.overrideDate(timeEntry.end, date);
        timeEntry.menu_open = false;

        this.calc_end_and_duration(timeEntry);
    }

    calc_end_and_duration (timeEntry, use_current_time = false) {
        const timeEntryEnd = parseSysDateTime(timeEntry.end)
        const timeEntryStart = parseSysDateTime(timeEntry.start)

        if ((!isNaN(timeEntryEnd) || use_current_time) && !isNaN(timeEntryStart)) {
            let end = parseSysDateTime(timeEntry.end)
            let start = parseSysDateTime(timeEntry.start)
            if (isNaN(end)) {
                end = parseSysDateTime(this.overrideTime(timeEntry.start, formatSysDateTime(new Date())));
            }

            if (use_current_time) {
                end = parseSysDateTime(this.overrideDate(formatSysDateTime(new Date()), formatSysDateTime(end)))
            }

            end = parseSysDateTime(this.overrideDate(formatSysDateTime(end), formatSysDateTime(start)))
            if (end <= start) {
                end.setDate(end.getDate() + 1)
            }

            timeEntry.end = this.overrideDate(timeEntry.end, formatSysDate(end));
            timeEntry.duration = Math.floor(end.getTime() - start.getTime()) / 1000;
        }
    }

    getStartDate(timeEntry) {
        return formatSysDate(parseSysDate(timeEntry.start))
    }

    getStartTimeActions () {
        if (!this.lastTimeEntry) {
            return null
        }

        if (!this.lastTimeEntry.duration) {
            return {
                time: null,
                text: $t("Timer is running!")
            }
        }

        return {
            time: this.lastTimeEntry.end,
            text: $t("Continue time from {0}").format(formatTime(this.lastTimeEntry.end))
        }
    }

    isTimeEntryDateToday(timeEntry) {
        let now = parseSysDate(timeEntry.start)
        let date = new Date()

        return formatSysDate(now) === formatSysDate(date)
    }

    setStartTime(timeEntry, stringTime) {
        if (stringTime === '') {
            timeEntry.start = parseSysDate(timeEntry.start)
            return;
        }

        timeEntry.start = this.overrideTime(timeEntry.start, stringTime)
        //timeEntry[field + "_f"] = formatTimeFromStr(time);
        timeEntry.dirty = true;

        if (!this.isTimeEntryDateToday(timeEntry) && isNaN(parseSysTime(timeEntry.end))) {
            return
        }

        this.calc_end_and_duration(timeEntry);

        if (timeEntry.duration) {
            this.calc_end(timeEntry);
        } else {
            this.calc_duration(timeEntry);
        }
    }

    calc_duration (time_entry) {
        if (time_entry.id === 0 && time_entry.start) {
            this.calc_end_and_duration(time_entry, true);
        }
    };

    calc_end (time_entry) {
        let start = parseSysDateTime(time_entry.start);
        let end = new Date(start.getTime() + time_entry.duration * 1000);
        time_entry.end = formatSysDateTime(end);
    };

    calc_start (time_entry) {
        let end = parseSysDateTime(time_entry.end);
        let start = new Date(end.getTime() + time_entry.duration * -1000);
        time_entry.start = formatSysDateTime(start);
        time_entry.start_time_error = null;
    };

    setEndTime(timeEntry, stringTime) {
        if (!stringTime) {
            timeEntry.end = null;
            return;
        }

        timeEntry.end = this.overrideTime(timeEntry.end, stringTime)
        timeEntry.dirty = true;

        this.calc_end_and_duration(timeEntry);
    }

    setDuration (time_entry, duration) {
        time_entry.duration_error = null;

        if (!duration) {
            time_entry.duration = null;
            return;
        }

        let seconds = parseTimeDuration(duration);
        if (seconds == null) {
            time_entry.duration_error = $t("Incorrect time");
            return;
        }

        time_entry.duration = seconds;
        time_entry.dirty = true;

        if (isNaN(parseSysTime(time_entry.start)) && isNaN(parseSysTime(time_entry.end))) {
            let now = parseSysDate(time_entry.start)
            let date = new Date()

            if (formatSysDate(now) === formatSysDate(date)) {
                now = date
                time_entry.end = formatSysDateTime(now)
                now.setSeconds(now.getSeconds() - time_entry.duration)
                time_entry.start = formatSysDateTime(now)
            } else {
                now.setHours(8)
                time_entry.start = formatSysDateTime(now)
                now.setSeconds(now.getSeconds() + time_entry.duration)
                time_entry.end = formatSysDateTime(now)
            }
        }

        if (isNaN(parseSysTime(time_entry.start))) this.calc_start(time_entry);
        else this.calc_end(time_entry);
    };

    setLastTime(time) {
        if (!time)
            return null;
        this.setStartTime(this.timeEntries[0], formatSysTime(parseSysDateTime(time)));
    }

    getDayDifference(timeEntry) {
        let start = parseSysDate(timeEntry.start);
        let end = parseSysDate(timeEntry.end);
        let diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return "";
        if (diffDays === 1) return $t("+1 day");
        return "{0}{1} days".format(diffDays >= 0 ? "+" : "-", Math.abs(diffDays));
    }

    getTimeEntryFormattedStart(timeEntry) {
        if (!timeEntry.start) {
            return ''
        }

        let sysTime = parseSysDateTime(timeEntry.start)

        if (isNaN(sysTime)) {
            return ''
        }

        return formatTime(sysTime)
    }

    getTimeEntryFormattedEnd(timeEntry) {
        if (!timeEntry.end) {
            return ''
        }

        let sysTime = parseSysDateTime(timeEntry.end)

        if (isNaN(sysTime)) {
            return ''
        }

        return formatTime(sysTime)
    }

    getTimeEntryFormattedDuration(timeEntry) {
        if (!timeEntry.duration) {
            return ''
        }

        return formatTimeDuration(timeEntry.duration)
    }

    isEditTimeEntryDescriptionVisible() {
        return this.editTimeEntryIndex !== null
    }

    setEditTimeEntryIndex(index) {
        this.editTimeEntryIndex = index
    }

    setTimeEntryDescription(description) {
        const timeEntry = this.timeEntries[this.editTimeEntryIndex]

        if (timeEntry.description !== description) {
            timeEntry.description = description
        }
    }

    getEditTimeEntryDescription() {
        const timeEntry = this.timeEntries[this.editTimeEntryIndex]

        return timeEntry.description
    }

    validate_save_time_entry (time_entry, index) {
        this.timeEntryErrors[index] = {}

        let isValid = true;

        if (this.getTimeEntryFormattedStart(time_entry) === '') {
            this.timeEntryErrors[index].start = $t("Required field");
            isValid = false
        }

        return isValid;
    };

    overrideDate(sysBaseDate, sysDateOverride) {
        if (!sysBaseDate) {
            return sysDateOverride
        }

        let baseTime = parseSysDateTime(sysBaseDate)
        if (isNaN(baseTime)) {
            return sysDateOverride
        }

        let dateOverride = parseSysDateTime(sysDateOverride)
        if (isNaN(dateOverride)) {
            dateOverride = parseSysDate(sysDateOverride)
        }

        dateOverride.setHours(baseTime.getHours())
        dateOverride.setMinutes(baseTime.getMinutes())
        dateOverride.setSeconds(baseTime.getSeconds())

        return formatSysDateTime(dateOverride)
    }

    overrideTime(sysBaseTime, sysTimeOverride) {
        if (!sysBaseTime) {
            let timeOverride = parseTime(sysTimeOverride)
            timeOverride = timeOverride ? parseSysTime(timeOverride) : parseSysTime(sysTimeOverride)
            return formatSysTime(timeOverride)
        }

        let baseTime = parseSysDate(sysBaseTime)
        if (isNaN(baseTime)) {
            return sysTimeOverride
        }

        let timeOverride = parseTime(sysTimeOverride)
        timeOverride = timeOverride ? parseSysTime(timeOverride) : parseSysTime(sysTimeOverride)

        baseTime.setHours(timeOverride.getHours())
        baseTime.setMinutes(timeOverride.getMinutes())
        baseTime.setSeconds(timeOverride.getSeconds())

        return formatSysDateTime(baseTime)
    }

    setRunningTaskId(value) {
        this.runningTaskId = value
    }
}
